import { create } from 'zustand';

import { initial_column_configs } from '@/configs/initial-column-configs';
import {
  COLUMN_CONFIG_KEY,
  RESET_COLUMN_KEY,
} from '@/configs/local-storage-keys';
import useUserStore from '@/stores/UserStore';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import { toast } from '../pure/ToastStore';

type States = {
  columnConfigs: Record<VIEW_CATEGORY, ColumnConfigsResT[]>;
};

type Actions = {
  setState: <T extends keyof States>(state: Record<T, States[T]>) => void;
  getColumnConfigs: () => void;
  toggleColumnVisible: (view_category: VIEW_CATEGORY, key: string) => void;
  changeColumnWidth: (
    view_category: VIEW_CATEGORY,
    key: string,
    width: number,
  ) => void;
  changeColumnOrder: (
    view_category: VIEW_CATEGORY,
    key: string,
    order: number,
  ) => void;
  initColumns: () => void;
};

const initialState: States = {
  columnConfigs: {
    feedback_view: [],
    account_view: [],
    contact_view: [],
    agent_view: [],
  },
};

// 로컬스토리지에 저장된 workspace별 culumn config 초기화를 위한 reset value
// 필드 추가 및 삭제 등 모델 변경 시 초기화 필요
const RESET_VALUE = '251222';

const useColumnConfigStore = create<States & Actions>((set, get) => ({
  ...initialState,
  setState: set,

  getColumnConfigs() {
    const wa = getCurrWorkspaceAlias();
    const userAlias = useUserStore.getState().user?.user_id || 'guest';

    const reset = localStorage.getItem(
      `${RESET_COLUMN_KEY}_${wa}_${userAlias}`,
    );

    if (reset !== RESET_VALUE) {
      localStorage.removeItem(`${COLUMN_CONFIG_KEY}_${wa}_${userAlias}`);
      localStorage.setItem(
        `${RESET_COLUMN_KEY}_${wa}_${userAlias}`,
        RESET_VALUE,
      );
      toast('Feedback column config has been reset.', 'success');
    }

    const configStr = localStorage.getItem(
      `${COLUMN_CONFIG_KEY}_${wa}_${userAlias}`,
    );

    if (!configStr) {
      set({ columnConfigs: initial_column_configs });
      return;
    }
    set({ columnConfigs: JSON.parse(configStr) });
  },
  toggleColumnVisible(view_category, key) {
    const wa = getCurrWorkspaceAlias();
    const userAlias = useUserStore.getState().user?.user_id || 'guest';

    const newConfigs = { ...get().columnConfigs };
    const newViewConfigs = [...newConfigs[view_category]];
    const targetIndex = newViewConfigs.findIndex(
      (config) => config.name === key,
    );
    let params;
    if (targetIndex === -1) {
      params = {
        name: key,
        width: 200,
        order: newViewConfigs.length,
        visible: true,
      };
      newViewConfigs.push(params);
    } else {
      params = {
        name: key,
        visible: false,
      };
      newViewConfigs.splice(targetIndex, 1);
    }
    newConfigs[view_category] = newViewConfigs;

    localStorage.setItem(
      `${COLUMN_CONFIG_KEY}_${wa}_${userAlias}`,
      JSON.stringify(newConfigs),
    );

    set({ columnConfigs: newConfigs });
  },
  changeColumnWidth(view_category, key, width) {
    const wa = getCurrWorkspaceAlias();
    const userAlias = useUserStore.getState().user?.user_id || 'guest';

    const newConfigs = { ...get().columnConfigs };
    const newViewConfigs = [...newConfigs[view_category]];
    const targetIndex = newViewConfigs.findIndex(
      (config) => config.name === key,
    );
    newViewConfigs[targetIndex] = {
      ...newViewConfigs[targetIndex],
      width: width,
    };
    newConfigs[view_category] = newViewConfigs;

    localStorage.setItem(
      `${COLUMN_CONFIG_KEY}_${wa}_${userAlias}`,
      JSON.stringify(newConfigs),
    );
    set({ columnConfigs: newConfigs });
  },
  changeColumnOrder(view_category, key, order) {
    const wa = getCurrWorkspaceAlias();
    const userAlias = useUserStore.getState().user?.user_id || 'guest';

    const newConfigs = { ...get().columnConfigs };
    const newViewConfigs = [...newConfigs[view_category]].sort((a, b) => {
      return a.order - b.order;
    });
    const targetIndex = newViewConfigs.findIndex(
      (config) => config.name === key,
    );
    const newViewConfig = { ...newViewConfigs[targetIndex] };
    newViewConfigs.splice(targetIndex, 1);
    newViewConfigs.splice(order, 0, newViewConfig);

    const result = newViewConfigs.map((config, index) => ({
      ...config,
      order: index,
    }));

    newConfigs[view_category] = result;

    localStorage.setItem(
      `${COLUMN_CONFIG_KEY}_${wa}_${userAlias}`,
      JSON.stringify(newConfigs),
    );

    set({ columnConfigs: newConfigs });
  },
  initColumns: () => {
    set(initialState);
  },
}));

export default useColumnConfigStore;
